import type {
  CreateExperimentResponse,
  ListExperimentsResponse,
  MoveExperimentResponse,
} from "@wallyo/contracts/console/experiments";
import type {
  AddLeadResponse,
  CreateCampaignResponse,
  ListCampaignsResponse,
  ListLeadsResponse,
  ListReferralsResponse,
  MoveLeadResponse,
  SendCampaignResponse,
} from "@wallyo/contracts/console/growth";
import type {
  GetIncidentResponse,
  ListIncidentsResponse,
  OpenIncidentResponse,
  UpdateIncidentResponse,
} from "@wallyo/contracts/console/incidents";
import type { GetScreenCountsResponse, GetWorkspaceOverviewResponse } from "@wallyo/contracts/console/overview";
import type {
  ListCategoriesResponse,
  ListJobsResponse,
  ListMarketplaceReviewsResponse,
  ListProvidersResponse,
  MarketplaceOverviewResponse,
} from "@wallyo/contracts/console/marketplace";
import type {
  AssignRoleResponse,
  CreateRoleResponse,
  DeactivateStaffResponse,
  ReactivateStaffResponse,
  EditRoleResponse,
  EndStaffSessionsResponse,
  GetStaffMeResponse,
  InviteStaffResponse,
  ListAuditLogResponse,
  ListPermissionHoldersResponse,
  ListRolesResponse,
  ListStaffResponse,
  RemoveRoleResponse,
  SetRoleFeatureResponse,
  SetRolePermissionResponse,
  SetStaffPasswordResponse,
  SignInStaffResponse,
  SignInStaffWithGoogleResponse,
  SignOutStaffResponse,
  VerifyStaffCodeResponse,
} from "@wallyo/contracts/console";
import type { UpdateStaffSettingsResponse } from "@wallyo/contracts/console/dashboard";
import type {
  GetCustomerAccountResponse,
  RevealContactDetailsResponse,
  SearchAccountsResponse,
} from "@wallyo/contracts/console/accounts";
import type {
  AddCaseNoteResponse,
  ClaimSupportCaseResponse,
  CloseSupportCaseResponse,
  GetSupportCaseResponse,
  LinkCaseCustomerResponse,
  ListMyAccessRequestsResponse,
  ListSupportCasesResponse,
  LogCaseCallResponse,
  OpenSupportCaseResponse,
  ReassignSupportCaseResponse,
  ReplyToCaseResponse,
  SupportCaseDetail,
} from "@wallyo/contracts/console/cases";
import type {
  CreateDiscountResponse,
  CreatePromoResponse,
  EndDiscountResponse,
  EndPromoResponse,
  EndSubscriptionResponse,
  ExtendSubscriptionResponse,
  ListDiscountsResponse,
  ListPlanPricesResponse,
  ListPromosResponse,
  ListProSubscriptionsResponse,
  SetPlanPriceResponse,
} from "@wallyo/contracts/console/billing";
import type {
  AddCandidateResponse,
  AddCourseResponse,
  ClosePayrollResponse,
  EditJobResponse,
  EnrolResponse,
  ListCoursesResponse,
  ListJobOpeningsResponse,
  MoveCandidateResponse,
  OpenJobResponse,
  DecideTimeOffResponse,
  EditPayrollLineResponse,
  GetPayrollResponse,
  GetReviewCycleResponse,
  OpenPayrollResponse,
  OpenReviewCycleResponse,
  WriteReviewResponse,
  GetDirectoryPersonResponse,
  GetLeavingResponse,
  ListAccessReviewsResponse,
  ListStaffActionsResponse,
  UpdateActionEnablementsResponse,
  ListDirectoryResponse,
  ListTimeOffResponse,
  RequestTimeOffResponse,
} from "@wallyo/contracts/console/hr";
import type {
  CreateRuleVersionResponse,
  CreateTeamResponse,
  EditTeamResponse,
  GetTeamResponse,
  ListCustomerFeatureValuesResponse,
  ListCustomerFeaturesResponse,
  ListPermissionsResponse,
  ListRuleVersionsResponse,
  ListRulesResponse,
  ListStaffFeaturesResponse,
  ListTeamsResponse,
  ListUserSettingsResponse,
  RemoveCustomerFeatureValueResponse,
  SaveCustomerFeaturesResponse,
  SavePermissionsResponse,
  SaveStaffFeaturesResponse,
  SaveTeamMembersResponse,
  SaveUserSettingsResponse,
  SetCustomerFeatureValueResponse,
} from "@wallyo/contracts/console/configuration";
import type {
  DecideVerificationResponse,
  GetVerificationImagesResponse,
  GetVerificationResponse,
  ListVerificationsResponse,
  RejectVerificationResponse,
} from "@wallyo/contracts/console/verifications";
import type { GetHealthDashboardResponse } from "@wallyo/contracts/console/dashboard";
import type {
  ApprovePaymentRequestResponse,
  GetPaymentResponse,
  IssueCreditResponse,
  IssueRefundResponse,
  ListApprovalRequestsResponse,
  ListPaymentsResponse,
  MarkPaymentPaidResponse,
  RejectPaymentRequestResponse,
  SettleRefundResponse,
  WaiveFeeResponse,
} from "@wallyo/contracts/console/payments";
import type {
  AddReportEvidenceResponse,
  BanPermanentlyResponse,
  DecideAppealResponse,
  GetSafetyReportResponse,
  OpenSafetyReportResponse,
  IssueVerdictResponse,
  LiftSuspensionResponse,
  ListSafetyReportsResponse,
  PlaceLegalHoldResponse,
  ReferToPoliceResponse,
  ReleaseLegalHoldResponse,
  SetReportSeverityResponse,
  ShareDataWithPoliceResponse,
  SuspendPendingInvestigationResponse,
} from "@wallyo/contracts/console/safety";
import { ConsoleApiError, parseConsoleError } from "./errors";
import { newIdempotencyKey } from "./idempotency";
import type { SessionStore } from "./session";

/**
 * Every console endpoint answers under its own name (WALLIYO-0068), e.g.
 * `{ "getStaffMeResponse": { … } }`. A call names the key it expects, and the
 * type of what it gets back follows from that name.
 */
export type ConsoleResponses = {
  signInStaffResponse: SignInStaffResponse;
  signInStaffWithGoogleResponse: SignInStaffWithGoogleResponse;
  verifyStaffCodeResponse: VerifyStaffCodeResponse;
  signOutStaffResponse: SignOutStaffResponse;
  getStaffMeResponse: GetStaffMeResponse;
  updateStaffSettingsResponse: UpdateStaffSettingsResponse;
  listAuditLogResponse: ListAuditLogResponse;
  listStaffResponse: ListStaffResponse;
  inviteStaffResponse: InviteStaffResponse;
  deactivateStaffResponse: DeactivateStaffResponse;
  reactivateStaffResponse: ReactivateStaffResponse;
  endStaffSessionsResponse: EndStaffSessionsResponse;
  assignRoleResponse: AssignRoleResponse;
  removeRoleResponse: RemoveRoleResponse;
  listDirectoryResponse: ListDirectoryResponse;
  getDirectoryPersonResponse: GetDirectoryPersonResponse;
  getLeavingResponse: GetLeavingResponse;
  listAccessReviewsResponse: ListAccessReviewsResponse;
  listStaffActionsResponse: ListStaffActionsResponse;
  updateActionEnablementsResponse: UpdateActionEnablementsResponse;
  listJobOpeningsResponse: ListJobOpeningsResponse;
  openJobResponse: OpenJobResponse;
  editJobResponse: EditJobResponse;
  addCandidateResponse: AddCandidateResponse;
  moveCandidateResponse: MoveCandidateResponse;
  listCoursesResponse: ListCoursesResponse;
  addCourseResponse: AddCourseResponse;
  enrolResponse: EnrolResponse;
  getReviewCycleResponse: GetReviewCycleResponse;
  openReviewCycleResponse: OpenReviewCycleResponse;
  writeReviewResponse: WriteReviewResponse;
  getPayrollResponse: GetPayrollResponse;
  openPayrollResponse: OpenPayrollResponse;
  editPayrollLineResponse: EditPayrollLineResponse;
  closePayrollResponse: ClosePayrollResponse;
  listTimeOffResponse: ListTimeOffResponse;
  requestTimeOffResponse: RequestTimeOffResponse;
  decideTimeOffResponse: DecideTimeOffResponse;
  listTeamsResponse: ListTeamsResponse;
  getTeamResponse: GetTeamResponse;
  createTeamResponse: CreateTeamResponse;
  editTeamResponse: EditTeamResponse;
  saveTeamMembersResponse: SaveTeamMembersResponse;
  listRolesResponse: ListRolesResponse;
  createRoleResponse: CreateRoleResponse;
  editRoleResponse: EditRoleResponse;
  setRoleFeatureResponse: SetRoleFeatureResponse;
  setRolePermissionResponse: SetRolePermissionResponse;
  listPermissionHoldersResponse: ListPermissionHoldersResponse;
  setStaffPasswordResponse: SetStaffPasswordResponse;
  searchAccountsResponse: SearchAccountsResponse;
  getCustomerAccountResponse: GetCustomerAccountResponse;
  revealContactDetailsResponse: RevealContactDetailsResponse;
  askForPermissionResponse: SupportCaseDetail;
  listMyAccessRequestsResponse: ListMyAccessRequestsResponse;
  decideAccessRequestResponse: SupportCaseDetail;
  listSupportCasesResponse: ListSupportCasesResponse;
  openSupportCaseResponse: OpenSupportCaseResponse;
  getSupportCaseResponse: GetSupportCaseResponse;
  logCaseCallResponse: LogCaseCallResponse;
  claimSupportCaseResponse: ClaimSupportCaseResponse;
  reassignSupportCaseResponse: ReassignSupportCaseResponse;
  addCaseNoteResponse: AddCaseNoteResponse;
  replyToCaseResponse: ReplyToCaseResponse;
  closeSupportCaseResponse: CloseSupportCaseResponse;
  linkCaseCustomerResponse: LinkCaseCustomerResponse;
  listCustomerFeaturesResponse: ListCustomerFeaturesResponse;
  listCustomerFeatureValuesResponse: ListCustomerFeatureValuesResponse;
  saveCustomerFeaturesResponse: SaveCustomerFeaturesResponse;
  listStaffFeaturesResponse: ListStaffFeaturesResponse;
  listPermissionsResponse: ListPermissionsResponse;
  savePermissionsResponse: SavePermissionsResponse;
  listUserSettingsResponse: ListUserSettingsResponse;
  saveUserSettingsResponse: SaveUserSettingsResponse;
  saveStaffFeaturesResponse: SaveStaffFeaturesResponse;
  setCustomerFeatureValueResponse: SetCustomerFeatureValueResponse;
  removeCustomerFeatureValueResponse: RemoveCustomerFeatureValueResponse;
  listRulesResponse: ListRulesResponse;
  listRuleVersionsResponse: ListRuleVersionsResponse;
  createRuleVersionResponse: CreateRuleVersionResponse;
  listVerificationsResponse: ListVerificationsResponse;
  getVerificationResponse: GetVerificationResponse;
  getVerificationImagesResponse: GetVerificationImagesResponse;
  approveVerificationResponse: DecideVerificationResponse;
  rejectVerificationResponse: RejectVerificationResponse;
  listSafetyReportsResponse: ListSafetyReportsResponse;
  getSafetyReportResponse: GetSafetyReportResponse;
  openSafetyReportResponse: OpenSafetyReportResponse;
  addReportEvidenceResponse: AddReportEvidenceResponse;
  setReportSeverityResponse: SetReportSeverityResponse;
  suspendPendingInvestigationResponse: SuspendPendingInvestigationResponse;
  liftSuspensionResponse: LiftSuspensionResponse;
  issueVerdictResponse: IssueVerdictResponse;
  banPermanentlyResponse: BanPermanentlyResponse;
  decideAppealResponse: DecideAppealResponse;
  referToPoliceResponse: ReferToPoliceResponse;
  shareDataWithPoliceResponse: ShareDataWithPoliceResponse;
  placeLegalHoldResponse: PlaceLegalHoldResponse;
  releaseLegalHoldResponse: ReleaseLegalHoldResponse;
  listPaymentsResponse: ListPaymentsResponse;
  getPaymentResponse: GetPaymentResponse;
  issueRefundResponse: IssueRefundResponse;
  waiveFeeResponse: WaiveFeeResponse;
  markPaymentPaidResponse: MarkPaymentPaidResponse;
  issueCreditResponse: IssueCreditResponse;
  settleRefundResponse: SettleRefundResponse;
  listApprovalRequestsResponse: ListApprovalRequestsResponse;
  approvePaymentRequestResponse: ApprovePaymentRequestResponse;
  rejectPaymentRequestResponse: RejectPaymentRequestResponse;
  getHealthDashboardResponse: GetHealthDashboardResponse;
  getWorkspaceOverviewResponse: GetWorkspaceOverviewResponse;
  getScreenCountsResponse: GetScreenCountsResponse;
  listIncidentsResponse: ListIncidentsResponse;
  listExperimentsResponse: ListExperimentsResponse;
  createExperimentResponse: CreateExperimentResponse;
  moveExperimentResponse: MoveExperimentResponse;
  listLeadsResponse: ListLeadsResponse;
  addLeadResponse: AddLeadResponse;
  moveLeadResponse: MoveLeadResponse;
  listCampaignsResponse: ListCampaignsResponse;
  createCampaignResponse: CreateCampaignResponse;
  sendCampaignResponse: SendCampaignResponse;
  listReferralsResponse: ListReferralsResponse;
  getIncidentResponse: GetIncidentResponse;
  openIncidentResponse: OpenIncidentResponse;
  updateIncidentResponse: UpdateIncidentResponse;
  getMarketplaceOverviewResponse: MarketplaceOverviewResponse;
  listMarketplaceJobsResponse: ListJobsResponse;
  listMarketplaceProvidersResponse: ListProvidersResponse;
  listMarketplaceCategoriesResponse: ListCategoriesResponse;
  listMarketplaceReviewsResponse: ListMarketplaceReviewsResponse;
  listPlanPricesResponse: ListPlanPricesResponse;
  setPlanPriceResponse: SetPlanPriceResponse;
  listPromosResponse: ListPromosResponse;
  createPromoResponse: CreatePromoResponse;
  endPromoResponse: EndPromoResponse;
  listDiscountsResponse: ListDiscountsResponse;
  createDiscountResponse: CreateDiscountResponse;
  endDiscountResponse: EndDiscountResponse;
  listProSubscriptionsResponse: ListProSubscriptionsResponse;
  extendSubscriptionResponse: ExtendSubscriptionResponse;
  endSubscriptionResponse: EndSubscriptionResponse;
};

export type ResponseKey = keyof ConsoleResponses;

/** What the screen shows about the connection while reads retry (ADR-0016 §2.5). */
export type ConnectionState =
  | { status: "ok" }
  | { status: "retrying"; attempt: number; maxAttempts: number; retryInMs: number };

/** The methods that change something. Each carries an Idempotency-Key. */
export type WriteMethod = "POST" | "PUT" | "PATCH" | "DELETE";

export type WriteOptions = {
  /**
   * One key per user intent (a click), reused if that same intent is retried.
   * Omit it and the call gets a fresh key of its own.
   */
  idempotencyKey?: string;
};

export interface ConsoleClient {
  get<K extends ResponseKey>(path: string, responseKey: K): Promise<ConsoleResponses[K]>;
  post<K extends ResponseKey>(
    path: string,
    body: unknown,
    responseKey: K,
    options?: WriteOptions,
  ): Promise<ConsoleResponses[K]>;
  /** Any write: POST, PUT, PATCH or DELETE, with a JSON body and an Idempotency-Key. */
  write<K extends ResponseKey>(
    method: WriteMethod,
    path: string,
    body: unknown,
    responseKey: K,
    options?: WriteOptions,
  ): Promise<ConsoleResponses[K]>;
  /**
   * A file the server answers with instead of JSON (the payments CSV). One
   * attempt, never retried by itself: each download is audited on its own.
   */
  getFile(path: string): Promise<ConsoleFile>;
  connection: {
    get(): ConnectionState;
    subscribe(listener: (state: ConnectionState) => void): () => void;
  };
}

/** A downloaded file: its text, and the name the server gave it. */
export type ConsoleFile = { text: string; filename: string | null; contentType: string | null };

/** `attachment; filename="walliyo-payments-2026-09.csv"` → the file name. */
export function filenameOf(disposition: string | null): string | null {
  const match = disposition ? /filename="?([^";]+)"?/i.exec(disposition) : null;
  return match?.[1]?.trim() || null;
}

export type ConsoleClientOptions = {
  baseUrl: string;
  session: SessionStore;
  fetch?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
  newKey?: () => string;
  /** Waits between read attempts. Its length is the number of retries. */
  retryDelaysMs?: readonly number[];
  /** A request with no answer after this long counts as a network failure. */
  timeoutMs?: number;
};

export const DEFAULT_RETRY_DELAYS_MS = [1_000, 2_000, 4_000] as const;
const DEFAULT_TIMEOUT_MS = 20_000;
/** Gateways answering for a server that is down or restarting: worth another read. */
const RETRYABLE_STATUSES = new Set([502, 503, 504]);

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Thrown inside a single attempt when no usable answer came back. */
class NoAnswer extends Error {
  constructor(
    readonly status: number | null,
    readonly requestId: string | null,
  ) {
    super("no answer");
  }
}

export function createConsoleClient(options: ConsoleClientOptions): ConsoleClient {
  const doFetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const sleep = options.sleep ?? defaultSleep;
  const newKey = options.newKey ?? newIdempotencyKey;
  const retryDelays = options.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const baseUrl = options.baseUrl.replace(/\/+$/, "");

  let connection: ConnectionState = { status: "ok" };
  const connectionListeners = new Set<(state: ConnectionState) => void>();
  const setConnection = (next: ConnectionState) => {
    if (next.status === "ok" && connection.status === "ok") return;
    connection = next;
    connectionListeners.forEach((listener) => listener(next));
  };

  async function readJson(response: Response): Promise<unknown> {
    try {
      return await response.json();
    } catch {
      return undefined;
    }
  }

  /** What a failed answer means: a console error, a gateway to retry through, or something unexpected. */
  function failureOf(response: Response, parsed: unknown, headerRequestId: string | null): Error {
    const error = parseConsoleError(parsed);
    if (error) {
      const failure = new ConsoleApiError({
        code: error.code,
        reason: error.reason ?? null,
        message: error.message,
        requestId: error.requestId || headerRequestId,
        status: response.status,
      });
      if (failure.endsSession) options.session.clear();
      return failure;
    }
    if (RETRYABLE_STATUSES.has(response.status)) return new NoAnswer(response.status, headerRequestId);
    return new ConsoleApiError({
      code: "unexpected_response",
      message: `HTTP ${response.status} without a console error body`,
      requestId: headerRequestId,
      status: response.status,
    });
  }

  async function attempt<K extends ResponseKey>(
    method: "GET" | WriteMethod,
    path: string,
    responseKey: K,
    body: unknown,
    idempotencyKey: string | null,
  ): Promise<ConsoleResponses[K]> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const token = options.session.get();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
    if (method !== "GET") headers["Content-Type"] = "application/json";

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let response: Response;
    try {
      response = await doFetch(`${baseUrl}${path}`, {
        method,
        headers,
        body: method === "GET" ? undefined : JSON.stringify(body ?? {}),
        signal: controller.signal,
      });
    } catch {
      throw new NoAnswer(null, null);
    } finally {
      clearTimeout(timer);
    }

    const headerRequestId = response.headers.get("x-request-id");
    const parsed = await readJson(response);
    if (!response.ok) throw failureOf(response, parsed, headerRequestId);

    const named =
      typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>)[responseKey] : undefined;
    if (typeof named !== "object" || named === null) {
      throw new ConsoleApiError({
        code: "unexpected_response",
        message: `The answer has no "${responseKey}"`,
        requestId: headerRequestId,
        status: response.status,
      });
    }
    return named as ConsoleResponses[K];
  }

  async function write<K extends ResponseKey>(
    method: WriteMethod,
    path: string,
    body: unknown,
    responseKey: K,
    writeOptions?: WriteOptions,
  ): Promise<ConsoleResponses[K]> {
    // Never retried by itself: a write that got no answer may have happened.
    const idempotencyKey = writeOptions?.idempotencyKey ?? newKey();
    try {
      return await attempt(method, path, responseKey, body, idempotencyKey);
    } catch (error) {
      if (!(error instanceof NoAnswer)) throw error;
      throw new ConsoleApiError({
        code: "network_error",
        message: "No answer to a write; it may or may not have been done",
        requestId: error.requestId,
        status: error.status,
        outcomeUnknown: true,
      });
    }
  }

  return {
    async get(path, responseKey) {
      const maxAttempts = retryDelays.length + 1;
      for (let index = 0; ; index += 1) {
        try {
          const result = await attempt("GET", path, responseKey, undefined, null);
          setConnection({ status: "ok" });
          return result;
        } catch (error) {
          if (!(error instanceof NoAnswer)) {
            setConnection({ status: "ok" });
            throw error;
          }
          if (index >= retryDelays.length) {
            setConnection({ status: "ok" });
            throw new ConsoleApiError({
              code: "network_error",
              message: `No answer after ${maxAttempts} attempts`,
              requestId: error.requestId,
              status: error.status,
            });
          }
          const retryInMs = retryDelays[index];
          setConnection({ status: "retrying", attempt: index + 2, maxAttempts, retryInMs });
          await sleep(retryInMs);
        }
      }
    },

    async post(path, body, responseKey, writeOptions) {
      return write("POST", path, body, responseKey, writeOptions);
    },
    write,

    async getFile(path) {
      const headers: Record<string, string> = { Accept: "text/csv, application/json" };
      const token = options.session.get();
      if (token) headers.Authorization = `Bearer ${token}`;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      let response: Response;
      try {
        response = await doFetch(`${baseUrl}${path}`, { method: "GET", headers, signal: controller.signal });
      } catch {
        throw new ConsoleApiError({ code: "network_error", message: "No answer to the download" });
      } finally {
        clearTimeout(timer);
      }
      const headerRequestId = response.headers.get("x-request-id");
      if (!response.ok) {
        const failure = failureOf(response, await readJson(response), headerRequestId);
        if (failure instanceof NoAnswer) {
          throw new ConsoleApiError({
            code: "network_error",
            message: "No answer to the download",
            requestId: headerRequestId,
            status: response.status,
          });
        }
        throw failure;
      }
      return {
        text: await response.text(),
        filename: filenameOf(response.headers.get("content-disposition")),
        contentType: response.headers.get("content-type"),
      };
    },
    connection: {
      get: () => connection,
      subscribe(listener) {
        connectionListeners.add(listener);
        return () => connectionListeners.delete(listener);
      },
    },
  };
}
